import { ArrowUp } from 'lucide-react';
import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@shared/components/ui/cn';

import type { InteractionAnswer, InteractionQuestion } from '../types';

interface InteractionCardProps {
  question: InteractionQuestion;
  onAnswer: (answer: InteractionAnswer) => void;
}

/**
 * Renders an orchestrator MCQ: the prompt plus selectable options. A free-form
 * option ("Other") reveals a text input. Answering posts the structured
 * interaction decision back to the orchestrator.
 */
export function InteractionCard({ question, onAnswer }: InteractionCardProps) {
  const { t } = useTranslation('ai-cio');
  const [freeFormId, setFreeFormId] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');

  const fixedOptions = question.options.filter((o) => !o.isFreeForm);
  const freeFormOptions = question.options.filter((o) => o.isFreeForm);

  function choose(optionId: string, isFreeForm: boolean) {
    if (isFreeForm) {
      setFreeFormId(optionId);
      return;
    }
    onAnswer({ question, selection: optionId });
  }

  function submitFreeText() {
    const text = freeText.trim();
    if (!text || !freeFormId) return;
    onAnswer({ question, selection: freeFormId, text });
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitFreeText();
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      {question.prompt && <p className="text-sm font-medium text-foreground">{question.prompt}</p>}

      {fixedOptions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {fixedOptions.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => {
                choose(o.id, false);
              }}
              className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent"
            >
              {o.label}
            </button>
          ))}
        </div>
      )}

      {freeFormOptions.map((o) =>
        freeFormId === o.id ? (
          <div
            key={o.id}
            className="mt-3 flex items-center gap-2 rounded-md border bg-background px-3 py-1.5"
          >
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus -- focusing the just-revealed free-form input is expected
              autoFocus
              value={freeText}
              onChange={(e) => {
                setFreeText(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              placeholder={t('interaction.typeAnswer', 'Type your answer…')}
              aria-label={question.prompt || t('interaction.typeAnswer', 'Type your answer…')}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <button
              type="button"
              onClick={submitFreeText}
              disabled={!freeText.trim()}
              aria-label={t('interaction.submit', 'Submit')}
              className={cn(
                'flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground',
                !freeText.trim() && 'cursor-not-allowed opacity-50',
              )}
            >
              <ArrowUp className="size-3.5" />
            </button>
          </div>
        ) : (
          <button
            key={o.id}
            type="button"
            onClick={() => {
              choose(o.id, true);
            }}
            className="mt-3 block rounded-md border border-dashed bg-background px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            {o.label || t('interaction.other', 'Other (type a value)')}
          </button>
        ),
      )}
    </div>
  );
}
